import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaHeartbeat,
  FaInfoCircle,
  FaNotesMedical,
  FaRegCalendarAlt,
  FaSearch,
  FaUserCheck,
  FaUserClock,
  FaUserInjured,
} from "react-icons/fa";
import { FaArrowRotateRight } from "react-icons/fa6";

import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "./../../../components/Button/Button";

import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import useScrollToTop from "./../../../hooks/useScollToTop";

import {
  getAdherentsByUser,
  updateBlessures,
} from "./../../../services/adherentServices";

const INFO_TXT =
  "Pour un souci d’organisation, nous demandons aux parents de signaler les absences de plus d’un cours aux enseignants et au bureau. Merci de penser à remettre l’adhérent en actif dès son retour.";

const ABSENCE_TYPES = ["Absent(e)", "Malade", "Blessé(e)"];

const todayStart = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseDate = (value) => {
  if (!value) return null;

  const date =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDate = (value) => {
  const date = parseDate(value);

  if (!date) return "Non précisée";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toInputDate = (value) => {
  if (!value) return "";

  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

const getApiBaseUrl = () => {
  try {
    return (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  } catch {
    return "";
  }
};

const toAbsolutePhotoUrl = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) return value;

  return `${getApiBaseUrl()}${value}`;
};

const getAdherentName = (adherent) =>
  `${adherent?.prenom || ""} ${adherent?.nom || ""}`.trim();

const getAbsenceStatus = (adherent) => {
  const isAbsent = !!adherent?.is_blessed;
  const returnDate = parseDate(adherent?.date_reprise);
  const today = todayStart();

  if (!isAbsent) {
    return {
      key: "active",
      label: "Actif(ve)",
      description: "Présence normale",
      icon: FaUserCheck,
      badge:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      card: "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    };
  }

  if (returnDate && returnDate < today) {
    return {
      key: "returnLate",
      label: "Retour à confirmer",
      description: `Reprise prévue le ${formatDate(returnDate)}`,
      icon: FaExclamationTriangle,
      badge:
        "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20",
      card: "border-red-200 bg-red-50/70 dark:border-red-500/20 dark:bg-red-500/10",
    };
  }

  if (returnDate && returnDate.getTime() === today.getTime()) {
    return {
      key: "returnToday",
      label: "Reprise aujourd’hui",
      description: `Reprise prévue le ${formatDate(returnDate)}`,
      icon: FaClock,
      badge:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      card: "border-amber-200 bg-amber-50/70 dark:border-amber-500/20 dark:bg-amber-500/10",
    };
  }

  return {
    key: "absent",
    label: "Absent(e)",
    description: returnDate
      ? `Reprise prévue le ${formatDate(returnDate)}`
      : "Date de reprise non précisée",
    icon: FaUserInjured,
    badge:
      "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20",
    card: "border-orange-200 bg-orange-50/70 dark:border-orange-500/20 dark:bg-orange-500/10",
  };
};

function AbsencesHero({ stats, loading }) {
  return (
    <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 p-5 text-white shadow-xl shadow-slate-950/10 sm:p-6">
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-orange-500/30 blur-3xl" />

      <div className="relative">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-sky-200">
          <FaNotesMedical className="text-[0.7rem]" />
          Suivi des absences
        </p>

        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Absences, maladies & blessures
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-200">
              Signalez rapidement une absence et pensez à confirmer le retour
              dès que l’adhérent reprend les cours.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:min-w-[360px]">
            <HeroStat
              icon={FaUserCheck}
              label="Actifs"
              value={loading ? "…" : stats.active}
            />

            <HeroStat
              icon={FaUserInjured}
              label="Absents"
              value={loading ? "…" : stats.absent}
            />

            <HeroStat
              icon={FaExclamationTriangle}
              label="À vérifier"
              value={loading ? "…" : stats.toConfirm}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 text-center backdrop-blur">
      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
        <Icon className="text-sm text-white" />
      </div>

      <p className="mt-2 text-lg font-bold leading-none">{value}</p>
      <p className="mt-1 text-[11px] font-medium text-slate-300">{label}</p>
    </div>
  );
}

function InfoBanner() {
  return (
    <div className="rounded-[1.5rem] border border-sky-200 bg-sky-50/90 p-4 text-sm leading-6 text-slate-700 shadow-sm dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-slate-200">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-600 shadow-sm dark:bg-slate-900">
          <FaInfoCircle />
        </span>

        <p>{INFO_TXT}</p>
      </div>
    </div>
  );
}

function StatusBadge({ adherent }) {
  const status = getAbsenceStatus(adherent);
  const Icon = status.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${status.badge}`}
    >
      <Icon className="text-[10px]" />
      {status.label}
    </span>
  );
}

function SelectedAdherentCard({ adherent, onMarkReturned, busy }) {
  if (!adherent) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-5 text-center dark:border-slate-700 dark:bg-slate-900/60">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-950">
          <FaUserClock className="text-xl" />
        </div>

        <p className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-100">
          Sélectionnez un adhérent
        </p>

        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Son statut actuel s’affichera ici avec les actions disponibles.
        </p>
      </div>
    );
  }

  const status = getAbsenceStatus(adherent);
  const StatusIcon = status.icon;
  const canMarkReturned =
    adherent.is_blessed === true ||
    adherent.is_blessed === 1 ||
    adherent.is_blessed === "1" ||
    adherent.is_blessed === "true";

  return (
    <div
      className={`overflow-hidden rounded-[1.5rem] border p-4 shadow-sm ${status.card}`}
    >
      <div className="flex items-start gap-4">
        <img
          src={toAbsolutePhotoUrl(adherent.photo_url) || "/img/user_avatar.jpg"}
          alt={getAdherentName(adherent)}
          className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
          onError={(event) => {
            event.currentTarget.src = "/img/user_avatar.jpg";
          }}
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-slate-950 dark:text-white">
            {getAdherentName(adherent)}
          </p>

          <div className="mt-2">
            <StatusBadge adherent={adherent} />
          </div>

          <p className="mt-2 flex items-center gap-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
            <StatusIcon className="shrink-0" />
            {status.description}
          </p>
        </div>
      </div>

      {adherent.notes_adherent && (
        <div className="mt-4 rounded-2xl bg-white/70 p-3 text-xs leading-5 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950/60 dark:text-slate-300 dark:ring-slate-700">
          <span className="font-bold text-slate-800 dark:text-slate-100">
            Dernières notes :
          </span>{" "}
          <span className="line-clamp-3">{adherent.notes_adherent}</span>
        </div>
      )}

      <button
        type="button"
        onClick={onMarkReturned}
        disabled={!canMarkReturned || busy}
        className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-bold text-white shadow-lg shadow-emerald-200/60 transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-black/20"
      >
        <FaCheckCircle />
        {busy ? "Enregistrement…" : "Marquer comme repris"}
      </button>
    </div>
  );
}

function AdherentQuickList({
  adherents,
  selectedAdherentId,
  onSelect,
  search,
  setSearch,
  loading,
}) {
  const filtered = useMemo(() => {
    const q = search
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

    if (!q) return adherents;

    return adherents.filter((adherent) => {
      const text = `${adherent.prenom || ""} ${adherent.nom || ""}`
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");

      return text.includes(q);
    });
  }, [adherents, search]);

  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Mes adhérents
          </p>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Sélection rapide
          </p>
        </div>

        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {adherents.length}
        </span>
      </div>

      <div className="relative mt-3">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher..."
          className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-300 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
        />
      </div>

      <div className="mt-3 max-h-[360px] space-y-2 overflow-y-auto pr-1">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Aucun adhérent trouvé.
          </div>
        ) : (
          filtered.map((adherent) => {
            const active = String(selectedAdherentId) === String(adherent.id);
            const status = getAbsenceStatus(adherent);

            return (
              <button
                key={adherent.id}
                type="button"
                onClick={() => onSelect(adherent.id)}
                className={[
                  "w-full rounded-2xl border p-3 text-left transition",
                  active
                    ? "border-violet-300 bg-violet-50 ring-2 ring-violet-200 dark:border-violet-500/40 dark:bg-violet-500/10 dark:ring-violet-500/20"
                    : "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800",
                ].join(" ")}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={
                      toAbsolutePhotoUrl(adherent.photo_url) ||
                      "/img/user_avatar.jpg"
                    }
                    alt={getAdherentName(adherent)}
                    className="h-11 w-11 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    onError={(event) => {
                      event.currentTarget.src = "/img/user_avatar.jpg";
                    }}
                  />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                      {getAdherentName(adherent)}
                    </p>

                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                      {status.label}
                      {adherent.date_reprise
                        ? ` · ${formatDate(adherent.date_reprise)}`
                        : ""}
                    </p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function TypeButton({ value, active, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-bold ring-1 transition",
        active
          ? "bg-violet-600 text-white ring-violet-500 shadow-lg shadow-violet-200/60 dark:shadow-black/20"
          : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900",
      ].join(" ")}
    >
      <Icon />
      {value}
    </button>
  );
}

export default function AbsencesPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [isBlessed, setIsBlessed] = useState(true);
  const [absenceType, setAbsenceType] = useState("Absent(e)");
  const [dateReprise, setDateReprise] = useState("");
  const [motifAbsence, setMotifAbsence] = useState("");

  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        if (!user?.userId) {
          toast.error("Utilisateur non identifié.");
          return;
        }

        const data = await getAdherentsByUser(user.userId);
        const list = Array.isArray(data) ? data : [];

        setAdherents(list);

        if (list.length > 0 && !selectedAdherentId) {
          setSelectedAdherentId(String(list[0].id));
        }
      } catch (error) {
        console.error("Erreur récupération adhérents:", error);
        toast.error("Impossible de charger vos adhérents.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const selectedAdherent = useMemo(
    () =>
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ),
    [adherents, selectedAdherentId],
  );

  useEffect(() => {
    if (!selectedAdherent) return;

    const isCurrentlyAbsent =
      selectedAdherent.is_blessed === true ||
      selectedAdherent.is_blessed === 1 ||
      selectedAdherent.is_blessed === "1" ||
      selectedAdherent.is_blessed === "true";

    setIsBlessed(isCurrentlyAbsent);
    setDateReprise(
      isCurrentlyAbsent ? toInputDate(selectedAdherent.date_reprise) : "",
    );
    setMotifAbsence("");
    setAbsenceType("Absent(e)");
  }, [selectedAdherent]);

  const stats = useMemo(() => {
    return adherents.reduce(
      (acc, adherent) => {
        const status = getAbsenceStatus(adherent);

        if (status.key === "active") acc.active += 1;
        if (["absent", "returnToday", "returnLate"].includes(status.key)) {
          acc.absent += 1;
        }
        if (["returnToday", "returnLate"].includes(status.key)) {
          acc.toConfirm += 1;
        }

        return acc;
      },
      {
        active: 0,
        absent: 0,
        toConfirm: 0,
      },
    );
  }, [adherents]);

  const selectedStatus = selectedAdherent
    ? getAbsenceStatus(selectedAdherent)
    : null;

  const returnDateWarning = useMemo(() => {
    if (!dateReprise) return null;

    const date = parseDate(dateReprise);
    const today = todayStart();

    if (!date) return null;

    if (date < today) {
      return "La date de reprise est dépassée. Pensez à confirmer le retour si l’adhérent a repris.";
    }

    if (date.getTime() === today.getTime()) {
      return "La reprise est prévue aujourd’hui. Pensez à confirmer le retour après le cours.";
    }

    return null;
  }, [dateReprise]);

  const updateLocalAdherent = (adherentId, patch) => {
    setAdherents((previous) =>
      previous.map((adherent) =>
        adherent.id === adherentId
          ? {
              ...adherent,
              ...patch,
            }
          : adherent,
      ),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedAdherent) {
      toast.warn("Choisissez d’abord un adhérent.");
      return;
    }

    if (!isBlessed) {
      toast.warn(
        "Pour signaler une absence, activez le statut absent. Pour un retour, utilisez le bouton “Marquer comme repris”.",
      );
      return;
    }

    if (!motifAbsence.trim() && !dateReprise) {
      toast.warn("Merci de préciser au moins un motif ou une date de reprise.");
      return;
    }

    const today = new Date().toLocaleDateString("fr-FR");
    const formattedDate = dateReprise
      ? formatDate(dateReprise)
      : "non précisée";

    const newNote = `${absenceType} signalé(e) le ${today} jusqu'au ${formattedDate} : ${
      motifAbsence.trim() || "—"
    }`;

    const newNotes = selectedAdherent.notes_adherent
      ? `${selectedAdherent.notes_adherent} | ${newNote}`
      : newNote;

    try {
      setSaving(true);

      await updateBlessures(selectedAdherent.id, {
        is_blessed: true,
        date_reprise: dateReprise || null,
        notes_adherent: newNotes,
      });

      updateLocalAdherent(selectedAdherent.id, {
        is_blessed: true,
        date_reprise: dateReprise || null,
        notes_adherent: newNotes,
      });

      toast.success("Absence enregistrée avec succès.");
      setMotifAbsence("");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReturned = async () => {
    if (!selectedAdherent) return;

    const ok = window.confirm(
      `Confirmer la reprise pour ${selectedAdherent.prenom} ${selectedAdherent.nom} ?`,
    );

    if (!ok) return;

    const today = new Date().toLocaleDateString("fr-FR");
    const note = `Reprise signalée le ${today}`;

    const newNotes = selectedAdherent.notes_adherent
      ? `${selectedAdherent.notes_adherent} | ${note}`
      : note;

    try {
      setMarking(true);

      await updateBlessures(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      updateLocalAdherent(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      setIsBlessed(false);
      setDateReprise("");
      setMotifAbsence("");

      toast.success("Reprise enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Impossible d’enregistrer la reprise.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        

        <HeaderTitle
          role="Utilisateur"
          title="Signaler une absence"
          subtitle="Gère tes absences, maladies et blessures."
          icon={FaNotesMedical}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <AbsencesHero stats={stats} loading={loading} />

        <section className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <div className="border-b border-slate-200/70 bg-white/70 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Organisation du club
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  Absences / Maladies / Blessures
                </h2>
              </div>

              {/* <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
                <img
                  src="/img/blessed.webp"
                  alt="Judoka blessé"
                  className="h-14 w-14 rounded-2xl object-contain"
                  loading="lazy"
                />

                <div className="hidden sm:block">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Suivi rapide
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Pensez au retour
                  </p>
                </div>
              </div> */}
            </div>
          </div>

          <div className="p-3 sm:p-5 lg:p-6">
            <InfoBanner />

            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
              <AdherentQuickList
                adherents={adherents}
                selectedAdherentId={selectedAdherentId}
                onSelect={(id) => setSelectedAdherentId(String(id))}
                search={search}
                setSearch={setSearch}
                loading={loading}
              />

              <div className="space-y-5">
                <SelectedAdherentCard
                  adherent={selectedAdherent}
                  onMarkReturned={handleMarkReturned}
                  busy={marking}
                />

                <form
                  onSubmit={handleSubmit}
                  className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Nouveau signalement
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        Indiquez le type d’absence, la date estimée de reprise
                        et un motif si nécessaire.
                      </p>
                    </div>

                    {selectedStatus && (
                      <div className="shrink-0">
                        <StatusBadge adherent={selectedAdherent} />
                      </div>
                    )}
                  </div>

                  <div className="mt-5">
                    <label className="mb-2 block text-sm font-bold text-slate-800 dark:text-slate-100">
                      Type de signalement
                    </label>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <TypeButton
                        value="Absent(e)"
                        icon={FaRegCalendarAlt}
                        active={absenceType === "Absent(e)"}
                        onClick={setAbsenceType}
                      />

                      <TypeButton
                        value="Malade"
                        icon={FaHeartbeat}
                        active={absenceType === "Malade"}
                        onClick={setAbsenceType}
                      />

                      <TypeButton
                        value="Blessé(e)"
                        icon={FaUserInjured}
                        active={absenceType === "Blessé(e)"}
                        onClick={setAbsenceType}
                      />
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/70">
                    <label className="flex cursor-pointer items-center justify-between gap-4">
                      <span>
                        <span className="block text-sm font-bold text-slate-900 dark:text-white">
                          L’adhérent est actuellement absent
                        </span>

                        <span className="mt-0.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                          Activez ce statut pour prévenir le bureau et les
                          enseignants.
                        </span>
                      </span>

                      <input
                        type="checkbox"
                        checked={isBlessed}
                        onChange={(event) => setIsBlessed(event.target.checked)}
                        className="h-5 w-5 accent-violet-600"
                      />
                    </label>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-bold text-slate-800 dark:text-slate-100">
                        Date de reprise estimée
                      </label>

                      <input
                        type="date"
                        className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        value={dateReprise}
                        onChange={(event) => setDateReprise(event.target.value)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-bold text-slate-800 dark:text-slate-100">
                        Motif d’absence
                      </label>

                      <input
                        type="text"
                        className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        placeholder="Grippe, genou, déplacement..."
                        value={motifAbsence}
                        onChange={(event) =>
                          setMotifAbsence(event.target.value)
                        }
                      />
                    </div>
                  </div>

                  {returnDateWarning && (
                    <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                      <FaExclamationTriangle className="mt-1 shrink-0" />
                      <p>{returnDateWarning}</p>
                    </div>
                  )}

                  <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsBlessed(true);
                        setAbsenceType("Absent(e)");
                        setDateReprise("");
                        setMotifAbsence("");
                      }}
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaArrowRotateRight />
                      Réinitialiser
                    </button>

                    <Button
                      type="submit"
                      text={
                        saving ? "Enregistrement…" : "Enregistrer l’absence"
                      }
                      icon={FaCalendarCheck}
                      className="!min-h-12 !rounded-2xl gap-2"
                      disabled={!selectedAdherent || saving}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <ToastContainer />
    </section>
  );
}
